import { ConfigApi, FetchApi } from '@backstage/core-plugin-api';
import {
  AnalysisApi,
  AnalysisRefused,
  AnalysisStartOutcome,
  AnalysisStarted,
  AnalysisStatus,
} from './AnalysisApi';

/**
 * Talks to IMC's on-demand analysis endpoints (feature 0040).
 *
 * Uses the same `fetchApi` as {@link ScoringDataJsonClient}, which attaches the Backstage identity
 * token — IMC gates these endpoints on exactly the token it already accepts for scorecard reads,
 * so no new auth path is involved.
 */
export class AnalysisClient implements AnalysisApi {
  private readonly configApi: ConfigApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { configApi: ConfigApi; fetchApi: FetchApi }) {
    this.configApi = options.configApi;
    this.fetchApi = options.fetchApi;
  }

  isEnabled(): boolean {
    return Boolean(this.baseUrl());
  }

  async start(systemName: string): Promise<AnalysisStartOutcome> {
    const response = await this.fetchApi.fetch(this.urlFor(systemName), {
      method: 'POST',
    });

    if (response.status === 202) {
      return { admitted: true, run: (await response.json()) as AnalysisStarted };
    }

    // A refusal is an answer, not a failure: IMC is telling the reader why not, in words the strip
    // renders. Anything else genuinely is broken and throws.
    if (response.status === 409) {
      return {
        admitted: false,
        refused: (await response.json()) as AnalysisRefused,
      };
    }

    throw new Error(
      `Could not start the analysis of '${systemName}' (status ${response.status}).`,
    );
  }

  async getStatus(systemName: string): Promise<AnalysisStatus | undefined> {
    const response = await this.fetchApi.fetch(this.urlFor(systemName));

    // A System IMC does not know is not an error here — the score tab renders for any System in the
    // catalog, including one IMC has never synced.
    if (response.status === 404) {
      return undefined;
    }

    if (!response.ok) {
      throw new Error(
        `Could not read the analysis status of '${systemName}' (status ${response.status}).`,
      );
    }

    return (await response.json()) as AnalysisStatus;
  }

  private baseUrl(): string | undefined {
    return this.configApi.getOptionalString('scorecards.analysisUrl');
  }

  private urlFor(systemName: string): string {
    const base = this.baseUrl();
    if (!base) {
      throw new Error(
        'scorecards.analysisUrl is not configured; on-demand analysis is unavailable.',
      );
    }

    return `${base.replace(/\/$/, '')}/system/${encodeURIComponent(systemName)}`;
  }
}
